/**
 * Primeiros Passos — o que falta cadastrar para a operação funcionar, em que ordem, e o
 * próximo clique (spec #387).
 *
 * A tarefa da tela: ver onde a operação parou e ir direto ao próximo passo. Um único botão
 * `contained` (o do cartão "Próximo passo"); embaixo, um bloco por trilha, na ordem que o
 * servidor mandou. O texto, a rota e o que cada rota exige moram em `textos.ts`.
 */

import React from 'react';
import { Box, Button, Card, CardContent, Chip, Skeleton, Typography } from '@mui/material';
import { InfoOutlined as InfoIcon, Refresh as RefreshIcon } from '@mui/icons-material';

import { PageShell } from '../../components';
import { useOnboarding } from '../../hooks/useOnboarding';
import { useManifesto } from '../../stores/manifestoStore';
import type { OnboardingStatus } from '../../types/onboarding';
import { NextStepCard } from './components/NextStepCard';
import { TrackCard } from './components/TrackCard';
import { concluido, progresso, proximoPasso } from './proximoPasso';
import {
  AVISO_DA_PLATAFORMA,
  AVISOS,
  ESTADOS_DA_TELA,
  ROTULO_DO_MODO,
  SUBTITULO_DA_PAGINA,
} from './textos';

const BREADCRUMBS = [{ label: 'Início', path: '/' }, { label: 'Primeiros Passos' }];

const plural = (n: number, um: string, varios: string): string => `${n} ${n === 1 ? um : varios}`;

function Aviso({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <Box
      role="note"
      sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 2, borderRadius: 1, bgcolor: 'action.hover' }}
    >
      <InfoIcon fontSize="small" sx={{ mt: 0.25 }} />
      <Typography sx={{ fontSize: '16px' }}>
        <b>{titulo}</b> {texto}
      </Typography>
    </Box>
  );
}

function Escopo({ status }: { status: OnboardingStatus }) {
  const { context, perspectiva } = status;
  if (!context.name) return null;

  const modo = status.tracks
    .flatMap((t) => t.items)
    .find((i) => i.key === 'service_mode' && i.mode)?.mode;

  const pilulas: string[] = [];
  if (perspectiva === 'prestador') {
    pilulas.push(plural(context.companies, 'empresa atendida', 'empresas atendidas'));
    if (modo) pilulas.push(`Atende por ${ROTULO_DO_MODO[modo]}`);
  } else if (perspectiva === 'plataforma') {
    pilulas.push(plural(context.companies, 'empresa', 'empresas'));
    pilulas.push(plural(context.providers, 'clínica', 'clínicas'));
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>{context.name}</Typography>
      {pilulas.map((p) => (
        <Chip key={p} size="small" variant="outlined" label={p} />
      ))}
    </Box>
  );
}

function Carregando() {
  return (
    <Box role="status" aria-label={ESTADOS_DA_TELA.carregando}>
      {[40, 85, 70, 55].map((largura) => (
        <Skeleton key={largura} variant="text" width={`${largura}%`} height={28} sx={{ mb: 1 }} />
      ))}
    </Box>
  );
}

function CartaoDeExcecao({
  titulo,
  texto,
  children,
}: {
  titulo: string;
  texto: string;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 3 }}>
        <Typography component="h2" variant="h6" fontWeight={700}>
          {titulo}
        </Typography>
        <Typography sx={{ fontSize: '16px', mt: 1, mb: 2 }}>{texto}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}

export const OnboardingPage: React.FC = () => {
  const onboarding = useOnboarding();
  const { manifesto } = useManifesto();

  const status = onboarding.estado === 'pronto' ? onboarding.status : null;
  const perspectiva = status?.perspectiva ?? manifesto?.perspectiva ?? 'empresa';

  const renderizarCorpo = () => {
    if (onboarding.estado === 'carregando') return <Carregando />;

    if (onboarding.estado === 'erro') {
      return (
        <CartaoDeExcecao titulo={ESTADOS_DA_TELA.erro.titulo} texto={ESTADOS_DA_TELA.erro.texto}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onboarding.recarregar}
            sx={{ minHeight: 44, fontSize: '16px' }}
          >
            {ESTADOS_DA_TELA.erro.botao}
          </Button>
        </CartaoDeExcecao>
      );
    }

    const { tracks, notices } = onboarding.status;
    if (tracks.length === 0) {
      return (
        <CartaoDeExcecao
          titulo={ESTADOS_DA_TELA.semVinculo.titulo}
          texto={ESTADOS_DA_TELA.semVinculo.texto}
        >
          <Chip variant="outlined" color="info" icon={<InfoIcon />} label={ESTADOS_DA_TELA.semVinculo.chip} />
        </CartaoDeExcecao>
      );
    }

    const avisos = notices
      .filter((n) => n.code !== 'NO_BINDING')
      .map((n) => ({ chave: `${n.code}-${n.module ?? ''}`, ...AVISOS[n.code](n.module) }));

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Escopo status={onboarding.status} />

        {perspectiva === 'plataforma' && <Aviso {...AVISO_DA_PLATAFORMA} />}
        {avisos.map(({ chave, ...aviso }) => (
          <Aviso key={chave} {...aviso} />
        ))}

        <NextStepCard
          passo={proximoPasso(onboarding.status, manifesto)}
          perspectiva={perspectiva}
          progresso={progresso(onboarding.status)}
          concluido={concluido(onboarding.status)}
        />

        {tracks.map((trilha) => (
          <TrackCard key={trilha.key} trilha={trilha} perspectiva={perspectiva} manifesto={manifesto} />
        ))}
      </Box>
    );
  };

  return (
    <PageShell
      breadcrumbs={BREADCRUMBS}
      title="Primeiros Passos"
      subtitle={SUBTITULO_DA_PAGINA[perspectiva]}
    >
      {renderizarCorpo()}
    </PageShell>
  );
};

export default OnboardingPage;
